// IndexedDB Service for User Data Storage
// Provides robust local storage with sync capabilities

export interface IndexedDBConfig {
  databaseName: string;
  version: number;
  stores: {
    name: string;
    keyPath: string;
    indexes?: Array<{
      name: string;
      keyPath: string;
      options?: IDBIndexParameters;
    }>;
  }[];
}

export interface SyncStatus {
  lastSyncTime: string | null;
  pendingChanges: number;
  syncErrors: string[];
  isOnline: boolean;
}

export interface PendingSyncItem {
  id: string;
  table: string;
  operation: 'create' | 'update' | 'delete';
  data: any;
  timestamp: string;
  retryCount: number;
}

class IndexedDBService {
  private static instance: IndexedDBService;
  private db: IDBDatabase | null = null;
  private config: IndexedDBConfig;
  private syncStatus: SyncStatus = {
    lastSyncTime: null,
    pendingChanges: 0,
    syncErrors: [],
    isOnline: navigator.onLine
  };

  private constructor() {
    this.config = {
      databaseName: 'GreenCardESG',
      version: 5,
      stores: [
        {
          name: 'netZeroTargets',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'user_id' },
            { name: 'status', keyPath: 'status' },
            { name: 'targetType', keyPath: 'target_type' },
            { name: 'createdAt', keyPath: 'created_at' }
          ]
        },
        {
          name: 'userMetrics',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'userId' },
            { name: 'category', keyPath: 'category' },
            { name: 'createdAt', keyPath: 'createdAt' }
          ]
        },
        {
          name: 'userMaterials',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'userId' },
            { name: 'category', keyPath: 'category' },
            { name: 'createdAt', keyPath: 'createdAt' }
          ]
        },
        {
          name: 'reports',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'userId' },
            { name: 'type', keyPath: 'type' },
            { name: 'createdAt', keyPath: 'createdAt' }
          ]
        },
        {
          name: 'exports',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'userId' },
            { name: 'format', keyPath: 'format' },
            { name: 'createdAt', keyPath: 'createdAt' }
          ]
        },
        {
          name: 'pendingSync',
          keyPath: 'id',
          indexes: [
            { name: 'table', keyPath: 'table' },
            { name: 'timestamp', keyPath: 'timestamp' },
            { name: 'retryCount', keyPath: 'retryCount' }
          ]
        },
        {
          name: 'backups',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'userId' },
            { name: 'type', keyPath: 'type' },
            { name: 'createdAt', keyPath: 'createdAt' }
          ]
        },
        {
          name: 'riskAssessments',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'user_id' },
            { name: 'category', keyPath: 'risk_category' },
            { name: 'status', keyPath: 'status' },
            { name: 'createdAt', keyPath: 'created_at' }
          ]
        },
        {
          name: 'investments',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'user_id' },
            { name: 'type', keyPath: 'investment_type' },
            { name: 'status', keyPath: 'status' },
            { name: 'createdAt', keyPath: 'created_at' }
          ]
        },
        {
          name: 'initiatives',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'user_id' },
            { name: 'type', keyPath: 'initiative_type' },
            { name: 'status', keyPath: 'status' },
            { name: 'createdAt', keyPath: 'created_at' }
          ]
        },
        {
          name: 'organizations',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'user_id' },
            { name: 'adminUserId', keyPath: 'admin_user_id' },
            { name: 'status', keyPath: 'status' },
            { name: 'createdAt', keyPath: 'created_at' }
          ]
        },
        {
          name: 'user_profiles',
          keyPath: 'user_id',
          indexes: [
            { name: 'role', keyPath: 'role' },
            { name: 'createdAt', keyPath: 'created_at' },
            { name: 'updatedAt', keyPath: 'updated_at' }
          ]
        },
        {
          name: 'suppliers',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'user_id' },
            { name: 'type', keyPath: 'supplier_type' },
            { name: 'status', keyPath: 'status' },
            { name: 'createdAt', keyPath: 'created_at' }
          ]
        },
        {
          name: 'assessments',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'user_id' },
            { name: 'supplierId', keyPath: 'supplier_id' },
            { name: 'type', keyPath: 'assessment_type' },
            { name: 'status', keyPath: 'status' },
            { name: 'createdAt', keyPath: 'created_at' }
          ]
        },
        {
          name: 'assessmentTemplates',
          keyPath: 'id',
          indexes: [
            { name: 'userId', keyPath: 'user_id' },
            { name: 'name', keyPath: 'name' },
            { name: 'createdAt', keyPath: 'created_at' }
          ]
        }
      ]
    };

    // Set up online/offline listeners
    window.addEventListener('online', () => {
      this.syncStatus.isOnline = true;
      this.processPendingSync();
    });

    window.addEventListener('offline', () => {
      this.syncStatus.isOnline = false;
    });
  }

  public static getInstance(): IndexedDBService {
    if (!IndexedDBService.instance) {
      IndexedDBService.instance = new IndexedDBService();
    }
    return IndexedDBService.instance;
  }

  // Initialize IndexedDB
  async initialize(): Promise<{ success: boolean; error?: string }> {
    try {
      if (this.db) {
        return { success: true };
      }

      return new Promise((resolve) => {
        const request = indexedDB.open(this.config.databaseName, this.config.version);

        request.onerror = () => {
          resolve({ success: false, error: 'Failed to open IndexedDB' });
        };

        request.onsuccess = () => {
          this.db = request.result;
          resolve({ success: true });
        };

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;

          // Create object stores
          this.config.stores.forEach(storeConfig => {
            if (!db.objectStoreNames.contains(storeConfig.name)) {
              const objectStore = db.createObjectStore(storeConfig.name, {
                keyPath: storeConfig.keyPath
              });

              // Create indexes
              storeConfig.indexes?.forEach(indexConfig => {
                objectStore.createIndex(
                  indexConfig.name,
                  indexConfig.keyPath,
                  indexConfig.options
                );
              });
            }
          });
        };
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to initialize IndexedDB'
      };
    }
  }

  // Generic CRUD operations
  async add<T>(storeName: string, data: T): Promise<{ success: boolean; error?: string }> {
    try {
      await this.ensureInitialized();
      
      return new Promise((resolve) => {
        const transaction = this.db!.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.add(data);

        request.onsuccess = () => {
          resolve({ success: true });
        };

        request.onerror = () => {
          resolve({ success: false, error: 'Failed to add data' });
        };
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to add data'
      };
    }
  }

  async get<T>(storeName: string, key: string): Promise<{ success: boolean; data?: T; error?: string }> {
    try {
      await this.ensureInitialized();
      
      return new Promise((resolve) => {
        const transaction = this.db!.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(key);

        request.onsuccess = () => {
          resolve({ success: true, data: request.result });
        };

        request.onerror = () => {
          resolve({ success: false, error: 'Failed to get data' });
        };
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get data'
      };
    }
  }

  async getAll<T>(storeName: string, indexName?: string, key?: any): Promise<{ success: boolean; data?: T[]; error?: string }> {
    try {
      await this.ensureInitialized();
      
      return new Promise((resolve) => {
        const transaction = this.db!.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = indexName && key 
          ? store.index(indexName).getAll(key)
          : store.getAll();

        request.onsuccess = () => {
          resolve({ success: true, data: request.result });
        };

        request.onerror = () => {
          resolve({ success: false, error: 'Failed to get data' });
        };
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get data'
      };
    }
  }

  async update<T>(storeName: string, data: T): Promise<{ success: boolean; error?: string }> {
    try {
      await this.ensureInitialized();
      
      return new Promise((resolve) => {
        const transaction = this.db!.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.put(data);

        request.onsuccess = () => {
          resolve({ success: true });
        };

        request.onerror = () => {
          resolve({ success: false, error: 'Failed to update data' });
        };
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update data'
      };
    }
  }

  async delete(storeName: string, key: string): Promise<{ success: boolean; error?: string }> {
    try {
      await this.ensureInitialized();
      
      return new Promise((resolve) => {
        const transaction = this.db!.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.delete(key);

        request.onsuccess = () => {
          resolve({ success: true });
        };

        request.onerror = () => {
          resolve({ success: false, error: 'Failed to delete data' });
        };
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete data'
      };
    }
  }

  async clear(storeName: string): Promise<{ success: boolean; error?: string }> {
    try {
      await this.ensureInitialized();
      
      return new Promise((resolve) => {
        const transaction = this.db!.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.clear();

        request.onsuccess = () => {
          resolve({ success: true });
        };

        request.onerror = () => {
          resolve({ success: false, error: 'Failed to clear data' });
        };
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to clear data'
      };
    }
  }

  // Sync operations
  async addToPendingSync(item: PendingSyncItem): Promise<void> {
    await this.add('pendingSync', item);
    this.syncStatus.pendingChanges++;
  }

  async processPendingSync(): Promise<void> {
    if (!this.syncStatus.isOnline) return;

    const { data: pendingItems } = await this.getAll<PendingSyncItem>('pendingSync');
    if (!pendingItems || pendingItems.length === 0) return;

    for (const item of pendingItems) {
      try {
        // Attempt to sync with server
        await this.syncWithServer(item);
        
        // Remove from pending sync on success
        await this.delete('pendingSync', item.id);
        this.syncStatus.pendingChanges--;
      } catch (error) {
        // Increment retry count
        item.retryCount++;
        if (item.retryCount < 5) {
          await this.update('pendingSync', item);
        } else {
          // Remove after max retries
          await this.delete('pendingSync', item.id);
          this.syncStatus.pendingChanges--;
          this.syncStatus.syncErrors.push(`Failed to sync ${item.table}: ${error}`);
        }
      }
    }

    this.syncStatus.lastSyncTime = new Date().toISOString();
  }

  private async syncWithServer(item: PendingSyncItem): Promise<void> {
    // This would integrate with your existing services
    // For now, we'll just log the sync attempt
    console.log('Syncing with server:', item);
    
    // TODO: Implement actual server sync
    // Example:
    // switch (item.operation) {
    //   case 'create':
    //     await organizationalDataService.createItem(item.table, item.data);
    //     break;
    //   case 'update':
    //     await organizationalDataService.updateItem(item.table, item.id, item.data);
    //     break;
    //   case 'delete':
    //     await organizationalDataService.deleteItem(item.table, item.id);
    //     break;
    // }
  }

  // Backup operations
  async createBackup(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const backupData: any = {};
      
      // Collect all data from all stores
      for (const store of this.config.stores) {
        if (store.name !== 'pendingSync') { // Don't backup pending sync
          const { data } = await this.getAll(store.name);
          backupData[store.name] = data || [];
        }
      }

      const backup = {
        id: `backup_${Date.now()}`,
        timestamp: new Date().toISOString(),
        data: backupData,
        version: this.config.version
      };

      await this.add('backups', backup);
      
      return { success: true, data: backup };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create backup'
      };
    }
  }

  async restoreFromBackup(backupId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: backup } = await this.get('backups', backupId);
      if (!backup) {
        return { success: false, error: 'Backup not found' };
      }

      // Clear existing data
      for (const store of this.config.stores) {
        if (store.name !== 'backups' && store.name !== 'pendingSync') {
          const { data: existingData } = await this.getAll(store.name);
          for (const item of existingData || []) {
            await this.delete(store.name, item.id);
          }
        }
      }

      // Restore data
      for (const [storeName, data] of Object.entries(backup.data)) {
        for (const item of data as any[]) {
          await this.add(storeName, item);
        }
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to restore backup'
      };
    }
  }

  // Utility methods
  private async ensureInitialized(): Promise<void> {
    if (!this.db) {
      const result = await this.initialize();
      if (!result.success) {
        throw new Error(result.error);
      }
    }
  }

  getSyncStatus(): SyncStatus {
    return { ...this.syncStatus };
  }

  async getStorageUsage(): Promise<{ used: number; available: number }> {
    // This is a simplified version - in practice you'd need to calculate actual usage
    return {
      used: 0, // TODO: Calculate actual usage
      available: 0 // TODO: Get available space
    };
  }

  async clearAllData(): Promise<{ success: boolean; error?: string }> {
    try {
      for (const store of this.config.stores) {
        const { data } = await this.getAll(store.name);
        for (const item of data || []) {
          await this.delete(store.name, item.id);
        }
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to clear data'
      };
    }
  }
}

export const indexedDBService = IndexedDBService.getInstance(); 